// components/AllUserComponent.jsx
import { useState, useEffect, useCallback } from 'react';
import { 
    Row, Col, Button, Image, Modal, Pagination, ConfigProvider, Tag, 
    Input, Select, Space, Card, message 
} from 'antd';
import { 
    PlusOutlined, EditOutlined, DeleteOutlined, SolutionOutlined, 
    SortAscendingOutlined, SortDescendingOutlined, SearchOutlined,
    FilterOutlined, ReloadOutlined 
} from '@ant-design/icons';
//import { faIR } from 'antd/lib/locale/fa_IR';
import faIR from 'antd/locale/fa_IR';
import { GetAll_Paging_User_Api, Delete_User_Api, GetUsersPagedAdvanced_Api } from '../../API/userApi';

const { Option } = Select;
const { Search } = Input;

export const AllUserComponent = ({
    setLoading,
    showMessage,
    version,
    currentPage,
    setCurrentPage,
    onAddClick,
    onEdit,
    onBackClick,
    onItemClick,
    onReservesClick,
    onRowDeleted 
}) => {
    const [users, setUsers] = useState([]);
    const [pageCount, setPageCount] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [filters, setFilters] = useState({
        sortField: 'CreateDate',
        sortDirection: 'desc',
        searchTerm: '',
        searchField: 'all'
    });

    useEffect(() => {
        loadData();
    }, [currentPage, version, filters]);

    const loadData = async () => {
        setLoading(true);
        try {
            let data;
            
            // اگر فیلترهای پیشرفته فعال هستند از API جدید استفاده کن
            if (filters.searchTerm || filters.sortField !== 'CreateDate') {
                const params = {
                    pageNumber: currentPage,
                    pageSize: 15,
                    sortField: filters.sortField,
                    sortDirection: filters.sortDirection,
                    searchTerm: filters.searchTerm,
                    searchField: filters.searchField
                };
                data = await GetUsersPagedAdvanced_Api(params);
            } else {
                // در غیر این صورت از API قدیمی استفاده کن
                data = await GetAll_Paging_User_Api({ 
                    pageNumber: currentPage 
                });
            }
            
            setLoading(false);
            
            if (data.messageStatus === "Successful") {
                setUsers(data.messageData.data);
                setPageCount(data.messageData.pageCount);
                setTotalCount(data.messageData.totalCount || (data.messageData.pageCount * 10));
            } else {
                showMessage(data.message, "error");
            }
        } catch (error) {
            setLoading(false);
            showMessage("خطا در ارتباط با سرور.", "error");
        }
    };

    const handleSort = (field) => {
        setFilters(prev => ({
            ...prev,
            sortField: field,
            sortDirection: prev.sortField === field && prev.sortDirection === 'asc' ? 'desc' : 'asc'
        }));
        setCurrentPage(1);
    };

    const handleSearch = (value) => {
        setFilters(prev => ({
            ...prev,
            searchTerm: value
        }));
        setCurrentPage(1);
    };

    const handleSearchFieldChange = (value) => {
        setFilters(prev => ({
            ...prev,
            searchField: value,
            searchTerm: '' // پاک کردن جستجو هنگام تغییر فیلد
        }));
    };

    const clearFilters = () => {
        setFilters({
            sortField: 'CreateDate',
            sortDirection: 'desc',
            searchTerm: '',
            searchField: 'all'
        });
        setCurrentPage(1);
    };

    const refreshData = () => {
        loadData();
    };

    const getSortIcon = (field) => {
        if (filters.sortField !== field) {
            return <SortAscendingOutlined style={{ opacity: 0.3 }} />;
        }
        return filters.sortDirection === 'asc' ? 
            <SortAscendingOutlined /> : 
            <SortDescendingOutlined />;
    };

    const SortableHeader = ({ field, children, sortable = true }) => (
        <div 
            style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '4px',
                cursor: sortable ? 'pointer' : 'default',
                justifyContent: 'flex-end'
            }}
            onClick={() => sortable && handleSort(field)}
        >
            {children}
            {sortable && getSortIcon(field)}
        </div>
    );

    const deleteRow = async (id, userName) => {
        try {
            setLoading(true);
            const data = await Delete_User_Api(id);
            setLoading(false);
            
            if (data.messageStatus === "Successful") {
                showMessage(`کاربر "${userName}" با موفقیت حذف شد`, "success");
                loadData(); // بارگذاری مجدد داده‌ها
                if (onRowDeleted) onRowDeleted();
            } else {
                showMessage(data.message, "error");
            }
        } catch (error) {
            setLoading(false);
            showMessage("خطا در ارتباط با سرور.", "error");
        }
    };

    const showDeleteConfirm = (userId, userName) => {
        Modal.confirm({
            title: 'تایید حذف',
            content: `آیا از حذف کاربر "${userName}" اطمینان دارید؟`,
            okText: 'بله، حذف شود',
            cancelText: 'انصراف',
            okType: 'danger',
            centered: true,
            onOk: () => deleteRow(userId, userName),
            footer: (_, { OkBtn, CancelBtn }) => (
                <>
                    <CancelBtn />
                    <OkBtn />
                </>
            ),
        });
    };

    const hasActiveFilters = filters.searchTerm || filters.sortField !== 'CreateDate';

    return (
        <>
            {/* هدر صفحه */}
            <Row className='sectionTitle' style={{ marginBottom: 16 }}>
                <Col xs={24}>
                    <Row justify="space-between" align="middle">
                        <Col xs={8} style={{ textAlign: "right" }}>
                            {/* فضای خالی برای تراز */}
                        </Col>
                        <Col xs={8} style={{ textAlign: "center" }}>
                            <h2 style={{ margin: 0 }}>
                                مدیریت کاربران 
                                {totalCount > 0 && <span style={{ fontSize: '0.8em', color: '#666' }}> ({totalCount} کاربر)</span>}
                            </h2>
                        </Col>
                        <Col xs={8} style={{ textAlign: "left" }}>
                            <Button 
                                type="primary" 
                                icon={<PlusOutlined />}
                                onClick={() => onAddClick()}
                            >
                                کاربر جدید
                            </Button>
                        </Col>
                    </Row>
                </Col>
            </Row>

            {/* کارت جستجو و فیلتر */}
            <Card 
                size="small" 
                style={{ marginBottom: 16 }}
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>جستجو و فیلتر</span>
                        <Space>
                            <Button 
                                icon={<ReloadOutlined />}
                                onClick={refreshData}
                                size="small"
                            >
                                بروزرسانی
                            </Button>
                            {hasActiveFilters && (
                                <Button 
                                    type="link" 
                                    onClick={clearFilters}
                                    size="small"
                                    danger
                                >
                                    حذف فیلترها
                                </Button>
                            )}
                        </Space>
                    </div>
                }
            >
                <Row gutter={[16, 16]} justify="center">
                    {/* فیلد جستجو */}
                    <Col xs={24} md={16} lg={12}>
                        <Space.Compact style={{ width: '100%' }}>
                            <Select 
                                value={filters.searchField}
                                onChange={handleSearchFieldChange}
                                style={{ width: '30%' }}
                                size="middle"
                            >
                                <Option value="all">همه فیلدها</Option>
                                <Option value="fullname">نام و نام خانوادگی</Option>
                                <Option value="nationalcode">کد ملی</Option>
                                <Option value="mobile">موبایل</Option>
                                <Option value="name">نام</Option>
                                <Option value="family">نام خانوادگی</Option>
                            </Select>
                            <Search
                                placeholder={
                                    filters.searchField === 'all' ? 'جستجو در همه فیلدها...' :
                                    filters.searchField === 'fullname' ? 'جستجو بر اساس نام و نام خانوادگی...' :
                                    filters.searchField === 'mobile' ? 'جستجو بر اساس کد ملی...' :
                                    filters.searchField === 'mobile' ? 'جستجو بر اساس موبایل...' :
                                    filters.searchField === 'mobile' ? 'جستجو بر اساس تام...' :
                                    'جستجو بر اساس نام خانوادگی...'
                                }
                                allowClear
                                value={filters.searchTerm}
                                onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
                                onSearch={handleSearch}
                                style={{ width: '70%' }}
                                size="middle"
                                enterButton={<SearchOutlined />}
                            />
                        </Space.Compact>
                    </Col>
                </Row>
            </Card>
            
            {/* لیست کاربران */}
            <div className='container'>
                {/* هدر جدول */}
                <Row className='titleRow'>
                    <Col xs={0} sm={0} md={2} lg={2} xl={2} className='titleColumn'>
                        تصویر
                    </Col>
                    <Col xs={6} sm={6} md={4} lg={4} xl={4} className='titleColumn'>
                        <SortableHeader field="fullName">
                            نام و نام خانوادگی
                        </SortableHeader>
                    </Col>
                    <Col xs={5} sm={5} md={3} lg={3} xl={3} className='titleColumn'>
                        <SortableHeader field="mobile">
                            موبایل
                        </SortableHeader>
                    </Col>
                    <Col xs={0} sm={0} md={2} lg={2} xl={2} className='titleColumn'>
                        <SortableHeader field="age">
                            سن
                        </SortableHeader>
                    </Col>
                    <Col xs={0} sm={0} md={3} lg={3} xl={3} className='titleColumn'>
                        <SortableHeader field="isVerifiedMobile">
                            وضعیت
                        </SortableHeader>
                    </Col>
                    <Col xs={13} sm={13} md={10} lg={10} xl={10} className='titleColumn'>
                        عملیات
                    </Col>
                </Row>

                {/* داده‌ها */}
                {users.length === 0 ? (
                    <Row style={{ padding: '40px', textAlign: 'center' }}>
                        <Col span={24}>
                            <div style={{ fontSize: '16px', color: '#999' }}>
                                {hasActiveFilters ? 
                                    '❌ نتیجه‌ای برای جستجوی شما یافت نشد' : 
                                    '📝 هیچ کاربری یافت نشد'
                                }
                            </div>
                        </Col>
                    </Row>
                ) : (
                    users.map((user) => (
                        <Row 
                            key={user.id} 
                            style={onItemClick ? { cursor: 'pointer' } : {}} 
                            onClick={() => { if (onItemClick) onItemClick(user.id, user.fullName); }} 
                            className='valueRow'
                        >
                            <Col xs={0} sm={0} md={2} lg={2} xl={2} className='valueColumn'>
                                <Image 
                                    width={35} 
                                    height={35} 
                                    src={user.photoUrl || '/default-avatar.png'} 
                                    preview={false}
                                    style={{ borderRadius: '50%' }}
                                    fallback="/default-avatar.png"
                                />
                            </Col>
                            <Col xs={6} sm={6} md={4} lg={4} xl={4} className='valueColumn'>
                                <div style={{ fontWeight: '500' }}>
                                    {user.fullName || `${user.name} ${user.family}`}
                                </div>
                            </Col>
                            <Col xs={5} sm={5} md={3} lg={3} xl={3} className='valueColumn'>
                                <div style={{ direction: 'ltr', textAlign: 'right' }}>
                                    {user.mobile}
                                </div>
                            </Col>
                            <Col xs={0} sm={0} md={2} lg={2} xl={2} className='valueColumn'>
                                {user.age ? `${user.age} سال` : '-'}
                            </Col>
                            <Col xs={0} sm={0} md={3} lg={3} xl={3} className='valueColumn'>
                                <Tag 
                                    color={user.isVerifiedMobile ? "green" : "red"}
                                    style={{ margin: 0 }}
                                >
                                    {user.isVerifiedMobile ? "تایید شده" : "تایید نشده"}
                                </Tag>
                            </Col>
                            <Col xs={13} sm={13} md={10} lg={10} xl={10} className='valueColumn' style={{ textAlign: "left" }}>
                                <Space direction="horizontal" size="small">
                                    <Button 
                                        size="small"
                                        icon={<SolutionOutlined />}
                                        onClick={(e) => { 
                                            e.stopPropagation();
                                            if (onReservesClick) onReservesClick(user.id, user.fullName);
                                        }}
                                    >
                                        ثبت‌نام‌ها
                                    </Button>
                                    <Button 
                                        type="primary" 
                                        size="small"
                                        icon={<EditOutlined />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onEdit(user.id, user.fullName);
                                        }}
                                    >
                                        ویرایش
                                    </Button>
                                    <Button 
                                        danger
                                        size="small"
                                        icon={<DeleteOutlined />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            showDeleteConfirm(user.id, user.fullName || `${user.name} ${user.family}`);
                                        }}
                                    >
                                        حذف
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    ))
                )}
            </div>
            
            {/* صفحه‌بندی */}
            <ConfigProvider direction="rtl" locale={faIR}>
                <div style={{ textAlign: 'center', marginTop: 24 }}>
                    <Pagination 
                        current={currentPage}
                        showSizeChanger={false}
                        showQuickJumper={false}
                        onChange={(page) => setCurrentPage(page)} 
                        total={pageCount * 10}
                        showTotal={(total, range) => 
                            `نمایش ${range[0]}-${range[1]} از ${totalCount} کاربر`
                        }
                    />
                </div>
            </ConfigProvider>
        </>
    );
};